import {
    HttpErrorResponse,
    HttpHandler,
    HttpHeaderResponse,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
    HttpResponse,
    HttpSentEvent,
    HttpUserEvent
} from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Observer, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { LocalCacheManager } from '../../managers/local-cache.manager';
import { LegendLogLevel, LoggingService } from '../logging.service';
import { AuthManagerService } from './auth-manager.service';
import { AuthenticatedUser } from './authenticated-user.model';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(private injector: Injector) { }

    private static isRefreshTokenInvalid(error: HttpErrorResponse): boolean {
        let parsedError: any = error.error;

        if (typeof error.error === 'string') {
            try {
                parsedError = JSON.parse(error.error);
            }
            catch {
                parsedError = error.error;
            }
        }
        const errorString =
            parsedError?.Error_1?.[0] ??
            parsedError?.error ??
            parsedError?.message ??
            '';

        if (
            typeof errorString === 'string' &&
            (errorString.includes('token is expired') || errorString.includes('another device'))
        ) {
            return true;
        }

        return false;
    }

    intercept(req: HttpRequest<any>, next: HttpHandler):
        Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
        const accessToken = LocalCacheManager.accessToken;
        let copiedRequest = req;

        if ((accessToken && accessToken !== '') &&
            !req.url.toLowerCase().includes('/auth/token') &&
            !req.url.toLowerCase().includes('blob.core.windows.net') &&
            !req.url.toLowerCase().includes('currency-api.com')) {
            copiedRequest = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
        }

        return next.handle(copiedRequest).pipe(catchError((error: Response, caught: Observable<any>) => {
            if (error instanceof HttpErrorResponse) {
                switch ((error as HttpErrorResponse).status) {
                    case 400:
                        return this.handle400Error(error);
                    case 401:
                        return this.handle401Error(req, next);
                    case 403:
                        LoggingService.log(LegendLogLevel.Error, 'User does not have access to requested resource');
                        return throwError(error);
                }

                return throwError(caught);
            }
            else {
                return throwError(caught);
            }
        }));
    }

    handle401Error(req: HttpRequest<any>, next: HttpHandler): any {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            this.tokenSubject.next(null);

            const loginAndUserManager = this.injector.get(AuthManagerService);

            return loginAndUserManager.loginWithRefreshToken(loginAndUserManager.currentUser.subsidiaryId,
                loginAndUserManager.currentUser.locationId)
                .pipe(switchMap((user: AuthenticatedUser) => {
                    this.tokenSubject.next(user.token);
                    const copiedRequest = req.clone({
                        setHeaders: {
                            Authorization: `Bearer ${user.token}`
                        }
                    });

                    return next.handle(copiedRequest);
                }),
                    catchError((error: any) => {
                        if (error instanceof HttpErrorResponse && ((error as HttpErrorResponse).status === 400)) {
                            // If there is an exception calling 'refreshToken', bad news so logout.
                            return this.handle400Error(error);
                        }

                        return throwError(error);
                    }),
                    finalize(() => {
                        this.isRefreshingToken = false;
                    })
                );
        }
        else {
            return this.tokenSubject.pipe(filter(token => token != null), take(1), switchMap(token => {
                const copiedRequest = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`
                    }
                });
                return next.handle(copiedRequest);
            }));
        }
    }

    handle400Error(error): any {
        if (error && error.status === 400 && AuthInterceptor.isRefreshTokenInvalid(error)) {
            this.isRefreshingToken = false;
            const authManager = this.injector.get(AuthManagerService);
            authManager.logout();
        }

        return throwError(error);
    }
}

function enterZone<T>(zone: NgZone): any {
    return (source: Observable<T>) => {
        return new Observable((sink: Observer<T>) => {
            return source.subscribe({
                next(x): void { zone.run(() => sink.next(x)); },
                error(e): void { zone.run(() => sink.error(e)); },
                complete(): void { zone.run(() => sink.complete()); }
            });
        });
    };
}
